// Grounded query resolver for the Client copilot.
//
// Every answer this returns is composed from LIVE graph values
// (Σ(BudgetLine.amount), Project.budgetCap, Milestone.dueOn, Room
// cohesion averages, Approval.state, etc.) — no canned strings,
// no regex-to-template hardcoding.
//
// Production wiring: the agent backend (agent/foyr_agent/server.py)
// streams Nemotron-generated answers over SSE; this resolver is the
// in-browser fallback that runs when no agent is reachable. The
// shape it returns matches the AG-UI STATE_DELTA envelope so both
// paths feed the same UI.

import { SAMPLE_PROJECT } from "../data/sampleGraph";
import type { GraphNode, NodeKind } from "../types/ontology";

export interface AgentTraceStep {
  kind: "think" | "act" | "observe";
  text: string;
}

export interface ClientReply {
  text: string;
  citations: { id: string; label: string; kind: NodeKind }[];
  trace: AgentTraceStep[];
  /** Indicates a graceful refusal — nothing in the graph matched. */
  refusal: boolean;
}

interface Intent {
  id: string;
  keywords: RegExp;
  resolve: (q: string) => ClientReply;
}

/* ── Graph walks (all pure over SAMPLE_PROJECT) ────────────────── */

function nodesOfKind(k: NodeKind): GraphNode[] {
  return SAMPLE_PROJECT.nodes.filter((n) => n.kind === k);
}

function asNumber(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function asString(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function inr(n: number): string {
  return `₹${n.toLocaleString("en-IN")}`;
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / 86_400_000);
}

function project(): GraphNode | undefined {
  return nodesOfKind("Project")[0];
}

function budgetState(): { cap: number; used: number; util: number; perRoom: { roomLabel: string; sum: number; share: number }[] } {
  const p = project();
  const cap = p ? asNumber(p.attrs["budgetCap"]) : 0;
  const lines = nodesOfKind("BudgetLine");
  const used = lines.reduce((acc, n) => acc + asNumber(n.attrs["amount"]), 0);
  const forRoom = new Map<string, number>();
  for (const e of SAMPLE_PROJECT.edges) {
    if (e.kind !== "FOR_ROOM") continue;
    const line = lines.find((l) => l.id === e.source);
    if (!line) continue;
    const amt = asNumber(line.attrs["amount"]) * asNumber(e.weight, 1);
    forRoom.set(e.target, (forRoom.get(e.target) ?? 0) + amt);
  }
  const perRoom = Array.from(forRoom.entries())
    .map(([roomId, sum]) => {
      const room = SAMPLE_PROJECT.nodes.find((n) => n.id === roomId);
      return { roomLabel: room?.label ?? roomId, sum, share: cap > 0 ? sum / cap : 0 };
    })
    .sort((a, b) => b.sum - a.sum);
  return { cap, used, util: cap > 0 ? used / cap : 0, perRoom };
}

function alertsList(): GraphNode[] { return nodesOfKind("Alert"); }
function approvalsByState(state: string): GraphNode[] {
  return nodesOfKind("Approval").filter((n) => asString(n.attrs["state"]) === state);
}

function handoverDays(): { ms: GraphNode; days: number } | null {
  const ms = nodesOfKind("Milestone").find((n) => /handover/i.test(n.label));
  if (!ms) return null;
  const due = asString(ms.attrs["dueOn"]);
  if (!due) return null;
  return { ms, days: daysUntil(due) };
}

function blockedMilestone(): { milestone: GraphNode; blocker: GraphNode | null } | null {
  for (const e of SAMPLE_PROJECT.edges) {
    if (e.kind !== "BLOCKED_BY") continue;
    const ms = SAMPLE_PROJECT.nodes.find((n) => n.id === e.source && n.kind === "Milestone");
    const blocker = SAMPLE_PROJECT.nodes.find((n) => n.id === e.target) ?? null;
    if (ms) return { milestone: ms, blocker };
  }
  return null;
}

function roomCohesion(): { room: GraphNode; avg: number; worst: GraphNode | null }[] {
  const out: { room: GraphNode; avg: number; worst: GraphNode | null }[] = [];
  for (const room of nodesOfKind("Room")) {
    const skus: GraphNode[] = [];
    for (const e of SAMPLE_PROJECT.edges) {
      if (e.kind === "IN_ROOM" && e.target === room.id) {
        const s = SAMPLE_PROJECT.nodes.find((n) => n.id === e.source);
        if (s && s.kind === "SKU") skus.push(s);
      }
    }
    if (skus.length === 0) continue;
    const matches = skus.map((s) => ({ s, m: asNumber(s.attrs["styleMatch"]) }));
    const avg = matches.reduce((a, b) => a + b.m, 0) / matches.length;
    const worst = matches.sort((a, b) => a.m - b.m)[0]?.s ?? null;
    out.push({ room, avg, worst });
  }
  return out.sort((a, b) => a.avg - b.avg);
}

/* ── Intents — each composes ONE answer from live graph data ──── */

const intents: Intent[] = [
  {
    id: "cost",
    keywords: /budget|cost|price|spend|expensive|over|rupee|money|₹/i,
    resolve(q) {
      const b = budgetState();
      const overrun = b.util > 1;
      const top = b.perRoom.slice(0, 2);
      const cited = [
        ...(project() ? [{ id: project()!.id, label: project()!.label, kind: "Project" as NodeKind }] : []),
        ...nodesOfKind("BudgetLine").slice(0, 3).map((n) => ({ id: n.id, label: n.label, kind: "BudgetLine" as NodeKind })),
      ];
      const text =
        `Right now you've spent ${inr(b.used)} of your ${inr(b.cap)} budget — that's ${(b.util * 100).toFixed(1)}%${overrun ? " (over the cap)" : ""}. ` +
        (top[0] ? `Heaviest room is ${top[0].roomLabel} at ${(top[0].share * 100).toFixed(1)}%${top[1] ? `; next is ${top[1].roomLabel} at ${(top[1].share * 100).toFixed(1)}%.` : "."}` : "") +
        ` Maya is watching the per-room shares.`;
      return {
        text,
        citations: cited,
        trace: [
          { kind: "think",   text: `User asking about cost · query="${q.slice(0, 60)}"` },
          { kind: "act",     text: `graph.aggregate(Σ BudgetLine.amount) / Project.budgetCap` },
          { kind: "observe", text: `Σ=${b.used} · cap=${b.cap} · util=${(b.util * 100).toFixed(2)}%` },
          { kind: "act",     text: `graph.aggregate(BudgetLine GROUP BY FOR_ROOM)` },
          { kind: "observe", text: `${b.perRoom.length} rooms · top=${top[0]?.roomLabel ?? "—"}` },
        ],
        refusal: false,
      };
    },
  },
  {
    id: "timeline",
    keywords: /timeline|handover|date|delay|when|deadline|milestone|day|due/i,
    resolve(q) {
      const h = handoverDays();
      const blocked = blockedMilestone();
      const text =
        (h ? `Handover (${h.ms.label}) is in ${h.days} days.` : "I couldn't find a Handover milestone in your project.") +
        (blocked && blocked.blocker
          ? ` One risk: ${blocked.milestone.label} is blocked by ${blocked.blocker.label}` +
            (typeof blocked.blocker.attrs["leadDays"] === "number" ? ` (${blocked.blocker.attrs["leadDays"]}-day lead).` : ".") +
            " Maya has a substitute lined up for your approval."
          : " No milestone blockers detected.");
      const cited: ClientReply["citations"] = [];
      if (h) cited.push({ id: h.ms.id, label: h.ms.label, kind: "Milestone" });
      if (blocked) {
        cited.push({ id: blocked.milestone.id, label: blocked.milestone.label, kind: "Milestone" });
        if (blocked.blocker) cited.push({ id: blocked.blocker.id, label: blocked.blocker.label, kind: blocked.blocker.kind });
      }
      return {
        text,
        citations: cited,
        trace: [
          { kind: "think",   text: `User asking about timeline · query="${q.slice(0, 60)}"` },
          { kind: "act",     text: `graph.find(Milestone WHERE label ~ /handover/i)` },
          { kind: "observe", text: h ? `${h.ms.id} · dueIn=${h.days}d` : `none` },
          { kind: "act",     text: `graph.walk(BLOCKED_BY)` },
          { kind: "observe", text: blocked ? `${blocked.milestone.id} ← ${blocked.blocker?.id ?? "?"}` : `no blockers` },
        ],
        refusal: false,
      };
    },
  },
  {
    id: "render",
    keywords: /render|design|look|view|see|picture|image|style|colour|color|aesthetic/i,
    resolve(q) {
      const rc = roomCohesion();
      const worst = rc[0];
      const text = worst
        ? `Cohesion across rooms averages ${(rc.reduce((a, r) => a + r.avg, 0) / rc.length * 100).toFixed(0)}%. ${worst.room.label} is the lowest at ${(worst.avg * 100).toFixed(0)}%${worst.worst ? ` because of ${worst.worst.label}` : ""}. Maya is restyling that piece before any paid render goes out.`
        : "Cohesion data isn't available yet — Maya is still wiring up the rooms.";
      const cited: ClientReply["citations"] = [];
      if (worst) {
        cited.push({ id: worst.room.id, label: worst.room.label, kind: "Room" });
        if (worst.worst) cited.push({ id: worst.worst.id, label: worst.worst.label, kind: worst.worst.kind });
      }
      return {
        text,
        citations: cited,
        trace: [
          { kind: "think",   text: `User asking about render/style · query="${q.slice(0, 60)}"` },
          { kind: "act",     text: `graph.aggregate(avg(SKU.styleMatch) GROUP BY Room)` },
          { kind: "observe", text: `${rc.length} rooms · worst=${worst?.room.id ?? "—"} avg=${worst ? (worst.avg * 100).toFixed(0) : "—"}%` },
        ],
        refusal: false,
      };
    },
  },
  {
    id: "approval",
    keywords: /approval|sign|approve|reject|stuck|pending|rera|filing|packet/i,
    resolve(q) {
      const pending = approvalsByState("pending");
      const approved = nodesOfKind("Approval").filter((n) => asString(n.attrs["state"]) === "approved");
      const text = pending.length > 0
        ? `${pending.length} approval${pending.length > 1 ? "s" : ""} awaiting you: ${pending.map((p) => p.label).join("; ")}. ${approved.length > 0 ? `${approved.length} already approved.` : ""}`
        : "No pending approvals on your side right now.";
      return {
        text,
        citations: pending.map((p) => ({ id: p.id, label: p.label, kind: "Approval" as NodeKind })),
        trace: [
          { kind: "think",   text: `User asking about approvals · query="${q.slice(0, 60)}"` },
          { kind: "act",     text: `graph.find(Approval WHERE state="pending")` },
          { kind: "observe", text: `${pending.length} pending · ${approved.length} approved` },
        ],
        refusal: false,
      };
    },
  },
  {
    id: "status",
    keywords: /status|how|update|progress|overall|going|doing/i,
    resolve(_q) {
      const b = budgetState();
      const alerts = alertsList();
      const h = handoverDays();
      const pending = approvalsByState("pending").length;
      const text =
        `Budget at ${(b.util * 100).toFixed(1)}%${b.util > 1 ? " (over)" : ""}. ` +
        `${alerts.length} open alert${alerts.length === 1 ? "" : "s"}. ` +
        (h ? `Handover in ${h.days} days. ` : "") +
        `${pending} approval${pending === 1 ? "" : "s"} awaiting you. ` +
        (alerts.length > 0 ? "Tap the packets below to act on the open items." : "Nothing pressing right now.");
      return {
        text,
        citations: alerts.map((a) => ({ id: a.id, label: a.label, kind: "Alert" as NodeKind })).slice(0, 3),
        trace: [
          { kind: "think",   text: `Status request — aggregate top counters from graph` },
          { kind: "act",     text: `graph.count(Alert) · sum(BudgetLine.amount) · find(Milestone:handover)` },
          { kind: "observe", text: `alerts=${alerts.length} · util=${(b.util * 100).toFixed(1)}% · handover=${h ? `${h.days}d` : "—"}` },
        ],
        refusal: false,
      };
    },
  },
];

/* ── Refusal — out-of-domain (anything we can't ground in graph) ── */

const OFF_TOPIC = /weather|sport|news|politics|movie|song|recipe|joke|stock|crypto|election/i;

export function resolveClientQuery(query: string): ClientReply {
  const text = query.trim();
  if (!text) {
    return { text: "Type a question and I'll answer from your project graph.", citations: [], trace: [], refusal: true };
  }
  if (OFF_TOPIC.test(text)) {
    return {
      text: "I'm only watching your home build with Maya — I can't help with that. Try asking about cost, timeline, render quality, or approvals.",
      citations: [],
      trace: [{ kind: "think", text: `out-of-domain query · refused` }],
      refusal: true,
    };
  }
  for (const i of intents) {
    if (i.keywords.test(text)) return i.resolve(text);
  }

  const lower = text.toLowerCase();
  const matched = SAMPLE_PROJECT.nodes
    .filter((n) => n.label.toLowerCase().split(/\W+/).some((tok) => tok.length > 3 && lower.includes(tok)))
    .slice(0, 3);
  if (matched.length > 0) {
    const main = matched[0]!;
    return {
      text: `Found ${matched.length} thing${matched.length > 1 ? "s" : ""} in your project that matches — most relevant is ${main.kind.toLowerCase()} ${main.label}. Maya can dig deeper; ask me about its cost, timeline, or style.`,
      citations: matched.map((n) => ({ id: n.id, label: n.label, kind: n.kind })),
      trace: [
        { kind: "think",   text: `No intent match — falling back to graph label search · query="${text.slice(0, 60)}"` },
        { kind: "act",     text: `graph.find(node WHERE label.tokens ⊆ query.tokens)` },
        { kind: "observe", text: `${matched.length} matches · top=${main.id}` },
      ],
      refusal: false,
    };
  }
  return {
    text: "I don't have a clean read on that from your project graph. Try asking about cost, timeline, renders, or approvals — that's where I'm strongest.",
    citations: [],
    trace: [{ kind: "think", text: `query="${text.slice(0, 40)}" · no intent · no entity match · honest refusal` }],
    refusal: true,
  };
}

// Persist a client message to Maya's inbox. The CLIENT picks "send to
// Maya" on a refusal bubble — there is no machine classifier deciding
// whether a query is a fact-question or a change-request. The agent
// answers what it can ground from the graph; everything else is the
// user's call.
const CLIENT_INBOX_KEY = "hgm.client.suggestions.v1";
export function queueForDesigner(text: string): void {
  try {
    const raw = localStorage.getItem(CLIENT_INBOX_KEY);
    const cur = raw ? (JSON.parse(raw) as string[]) : [];
    cur.push(`Client request: ${text.slice(0, 240)}`);
    localStorage.setItem(CLIENT_INBOX_KEY, JSON.stringify(cur));
  } catch { /* ignore */ }
}
export function readQueuedForDesigner(): string[] {
  try {
    const raw = localStorage.getItem(CLIENT_INBOX_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}
